import React, { useState } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import Textfield from '@atlaskit/textfield';
import AddIcon from '@atlaskit/icon/core/add';
import Form, { Field, ErrorMessage } from '@atlaskit/form';
import type { FormApi } from '@atlaskit/form';
import Spinner from '@atlaskit/spinner';
import { IconButton } from '@atlaskit/button/new';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';

import { useFetchDataFromORS } from '@confluence/embed-utils';

import { SourceErrors } from '../types/SourceError';
import type { SourceError } from '../types/SourceError';
import type { ResolvedAtlassianLink } from '../types/ResolvedAtlassianLink';
import type { AdditionalResource } from '../../../../../types/additionalResource';
import type { ActiveAudioContent } from '../../../../../types/activeAudioContent';
import { getConfluenceContentAri } from '../../../../../utils/getConfluenceContentAri';
import { isSupportedResource } from '../utils/supportedResourceTypes';

const i18n = defineMessages({
	focusedPlaceholder: {
		id: 'audio.audio-popup.sources-tab.focused-placeholder',
		defaultMessage: 'Paste a Confluence link...',
		description: 'Placeholder in a textbox when the user has the text box focused',
	},
	unfocusedPlaceholder: {
		id: 'audio.audio-popup.sources-tab.unfocused-placeholder',
		defaultMessage: 'Add a link...',
		description: 'Placeholder in a textbox when the textbox is not focused',
	},
	submitButtonLabel: {
		id: 'audio.audio-popup.sources-tab.submit-button-label',
		defaultMessage: 'Add Link',
		description: 'Label for the submit button',
	},
	clearButtonLabel: {
		id: 'audio.audio-popup.sources-tab.clear-button-label',
		defaultMessage: 'Clear',
		description: 'Label for the clear button',
	},
	[SourceErrors.UNSUPPORTED_URL]: {
		id: 'audio.audio-popup.sources-tab.validation-error.unsupported-url',
		defaultMessage: 'Only Confluence links are supported. More coming soon.',
		description:
			'Error message shown to the user when an invalid string or URL is pasted in to the textbox',
	},
	[SourceErrors.DUPLICATE]: {
		id: 'audio.audio-popup.sources-tab.validation-error.duplicate',
		defaultMessage: 'This link is already present!',
		description:
			'Error message shown to the user when they paste a link that has already been pasted.',
	},
	[SourceErrors.INVALID_URL]: {
		id: 'audio.audio-popup.sources-tab.validation-error.invalid-url',
		defaultMessage: 'Please paste a valid link',
		description: 'Error message shown to the user when they submit text that is not a link.',
	},
	[SourceErrors.CURRENT_CONTENT]: {
		id: 'audio.audio-popup.sources-tab.validation-error.current-content',
		defaultMessage: 'The active content is already included.',
		description: 'Error message shown to the user when they submit text that is not a link.',
	},
	[SourceErrors.NO_ACCESS]: {
		id: 'audio.audio-popup.sources-tab.validation-error.no-access',
		defaultMessage: 'This content is restricted.',
		description:
			'Error message shown to the user when they submit a link that they do not have access to.',
	},
});

const styles = cssMap({
	container: {
		flex: 1,
	},
	inputContainer: {
		paddingBlock: token('space.025'),
		position: 'relative',
	},
	addIcon: {
		height: '18px',
		marginInlineStart: token('space.025'),
		borderWidth: token('border.width'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	textFieldContainer: {
		flexGrow: 1,
		paddingBlock: token('space.negative.400'),
	},
	buttonsContainer: {
		position: 'absolute',
		insetInlineEnd: 0,
		marginBlockStart: token('space.075'),
		zIndex: 200,
	},
	buttonWrapper: {
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
	},
});

type FormValues = {
	resourceUrl: string;
};

const MAX_RESOURCES = 4;

const InputAddIcon = (
	<Box xcss={styles.addIcon}>
		<AddIcon label="" size="small" spacing="compact" />
	</Box>
);

export type SourcesInputProps = {
	cloudId: string;
	additionalResources: AdditionalResource[];
	addAdditionalResource: (newAdditionalResource: AdditionalResource) => void;
	activeContent?: ActiveAudioContent;
};

export const SourcesInput = ({
	cloudId,
	additionalResources,
	addAdditionalResource,
	activeContent,
}: SourcesInputProps) => {
	const { formatMessage } = useIntl();
	const { fetchEmbedData } = useFetchDataFromORS();
	const [inputError, setInputError] = useState<SourceError | undefined>();
	const [isFocused, setIsFocused] = useState(false);

	const isMaxReached = additionalResources.length === MAX_RESOURCES;
	const [placeholder, setPlaceholder] = useState<string>(formatMessage(i18n.unfocusedPlaceholder));

	const handleNewResourceSubmit = async (
		values: FormValues,
		form: FormApi<FormValues>,
	): Promise<void> => {
		// Clear our errors on every submit
		setInputError(undefined);
		const { resourceUrl } = values;
		// Is this a valid link
		try {
			new URL(resourceUrl);
		} catch (e) {
			form.reset();
			setInputError(SourceErrors.INVALID_URL);
			return undefined;
		}

		const resolvedLink = await resolveLink(resourceUrl);

		// Ensure that this is a supported resource
		const isSupported = isSupportedResource(resolvedLink);
		if (!isSupported) {
			form.reset();
			setInputError(SourceErrors.UNSUPPORTED_URL);
			return undefined;
		}
		const newResource: AdditionalResource = {
			url: resourceUrl,
			// we know that ari is not undefined because isSupportedResource would have returned false
			// if we didn't have Confluence as the product or page as the resource type
			ari: resolvedLink.ari!,
		};

		const isDuplicate = additionalResources.some(
			(existingResource) => existingResource.ari === newResource.ari,
		);
		if (isDuplicate) {
			form.reset();
			setInputError(SourceErrors.DUPLICATE);
			return undefined;
		}

		// Ensure the user has access to the content
		if (resolvedLink?.access !== 'granted') {
			form.reset();
			setInputError(SourceErrors.NO_ACCESS);
			return undefined;
		}

		// Ensure that we are not adding the active content
		// activeContent should really be there and have these fields
		// this is just a best effort check anyways
		const { contentType = '', id = '' } = activeContent || {};
		const currentAri = getConfluenceContentAri(cloudId, id, contentType);
		const submissionMatchesActiveContent = currentAri?.toString() === newResource.ari;
		if (submissionMatchesActiveContent) {
			form.reset();
			setInputError(SourceErrors.CURRENT_CONTENT);
			return undefined;
		}

		if (newResource) {
			form.reset();
			// Submission doesn't trigger the onBlur handler
			setPlaceholder(formatMessage(i18n.unfocusedPlaceholder));
			setIsFocused(false);
			addAdditionalResource(newResource);
		}
	};

	// Only returns resolved Atlassian links
	// Loom will make this a bit complicated down the line
	const resolveLink = async (value: string): Promise<ResolvedAtlassianLink> => {
		const linkData = await fetchEmbedData({ embedUrl: value });

		const resolvedAccess = linkData.meta?.access;
		const resolvedAri = linkData.data?.['atlassian:ari'];
		const resolvedProduct = linkData.meta?.product;
		const resolvedResourceType = linkData.meta?.resourceType;

		return {
			access: resolvedAccess,
			ari: resolvedAri,
			product: resolvedProduct,
			resourceType: resolvedResourceType,
		};
	};

	const handleOnFocus = () => {
		setPlaceholder(formatMessage(i18n.focusedPlaceholder));
		setInputError(undefined);
		setIsFocused(true);
	};

	const handleOnBlur = (event: React.FocusEvent) => {
		// Only blur if focus is not moving to our action buttons
		const relatedTarget = event.relatedTarget as HTMLElement;
		const isMovingToActionButton = relatedTarget?.closest('[data-testid*="action-button"]');

		if (!isMovingToActionButton) {
			setPlaceholder(formatMessage(i18n.unfocusedPlaceholder));
			setInputError(undefined);
			setIsFocused(false);
		}
	};

	const handleClear = (reset: () => void) => {
		reset();
		setInputError(undefined);
		setPlaceholder(formatMessage(i18n.unfocusedPlaceholder));
		setIsFocused(false);
	};

	return (
		<Form onSubmit={handleNewResourceSubmit}>
			{({ formProps, submitting, reset }) => (
				<form {...formProps}>
					<Field name="resourceUrl" defaultValue="" isDisabled={isMaxReached || submitting}>
						{({ fieldProps }) => (
							<Box xcss={styles.inputContainer}>
								<Textfield
									{...fieldProps}
									testId="audio-sources-textfield"
									elemAfterInput={submitting && <Spinner size="small" />}
									appearance="subtle"
									elemBeforeInput={InputAddIcon}
									aria-label={formatMessage(i18n.focusedPlaceholder)}
									isInvalid={!!inputError}
									placeholder={placeholder}
									isCompact
									onFocus={handleOnFocus}
									onBlur={handleOnBlur}
								/>
								{inputError && (
									<ErrorMessage>
										<FormattedMessage {...i18n[inputError]} />
									</ErrorMessage>
								)}
								{isFocused && !inputError && !submitting && (
									<Flex xcss={styles.buttonsContainer} gap="space.050">
										<Box xcss={styles.buttonWrapper}>
											<IconButton
												type="submit"
												icon={(iconProps) => <CheckMarkIcon {...iconProps} size="small" />}
												label={formatMessage(i18n.submitButtonLabel)}
												isDisabled={!fieldProps.value}
												testId="action-button-submit"
											/>
										</Box>
										<Box xcss={styles.buttonWrapper}>
											<IconButton
												icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
												label={formatMessage(i18n.clearButtonLabel)}
												isDisabled={!fieldProps.value}
												onClick={() => handleClear(reset)}
												testId="action-button-clear"
											/>
										</Box>
									</Flex>
								)}
							</Box>
						)}
					</Field>
				</form>
			)}
		</Form>
	);
};
